// @src/pre-deployment/src/App.js v1.9.59 - Refactored Admin & Logging Reconciliation
// - Main App component for pre-deployment environment with dynamic module routing
// - REMOVED: Top-level useDataService import; ChapterContainer extracted and lazy-loaded so tracking/contentDiscovery loads only on chapter route (sign-in page no longer blocked)
// - REMOVED: All references to deprecated folder; replaced with inline "Deprecated (removed)" doc block below
// - Fully dynamic based on /feature_testing/Korea_University/LearningModulesOrder/order
// - NEW: Uses single source of truth (LearningModulesOrder) and moduleMap
// - CRITICAL FIX: Proper error handling and fallback mechanisms
// - Uses new document structure with defaultOrder, level_X.chapter_Y pattern, and moduleMap
// - Enhanced with reusable error handling utilities and dynamic navigation
// - FIXED: Field naming consistency (moduleMap vs moduleMapping)
// - FIXED: Route redirection bug for chapter paths
// - FIXED: Dynamic component loading with proper fallbacks
// - FIXED: Component timeout reduced from 30s to 10s with auto-advance
// - FIXED: Component registry with dynamic imports and error recovery

import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import './componentdesign/index.css';
import './components/navigation/Skeleton.css';

// Auth
import EmailEntry from './components/auth/EmailEntry';
import PasswordSetup from './components/auth/PasswordSetup';
import ProfileComplete from './components/auth/ProfileComplete';
// Dev
import ComponentTestBench from './components/dev/ComponentTestBench';
import ComponentTestView from './components/dev/ComponentTestView';
// Exercises
import { writeRawErrorFromFailureContext } from './components/ErrorHandling/AutomatedErrorSystem/RawErrorService';
import ClozeExercise from './components/exercises/ClozeExercise';
import GrammarFeature from './components/exercises/GrammarFeature';
// ShapePuzzle and ShapePuzzleNew removed 
import TranslationMatching from './components/exercises/TranslationMatching';
// Learning - DialogueUniversal loaded lazily via componentRegistry (FIX-069: avoid TDZ from static import)
import GrammarLearn from './components/learning/GrammarLearn';
import KoreanAssessment from './components/learning/KoreanAssessment';
import VocabularyFeature from './components/learning/VocabularyFeature';
import VocabularyLearn from './components/learning/VocabularyLearn';
// Navigation
import AppHomepage from './components/navigation/AppHomepage';
import ChapterCompletion from './components/navigation/ChapterCompletion';
import ChapterReview from './components/navigation/ChapterReview';
import Dashboard from './components/navigation/Dashboard';
import LevelPage from './components/navigation/LevelPage';
import MapMenu from './components/navigation/MapMenu';
import ModernLevelSelect from './components/navigation/ModernLevelSelect';
// Shared
import About from './components/shared/About';
import ContactCard from './components/shared/ContactCard';
import ContactLanding from './components/shared/ContactLanding';
import SafeErrorBoundary from './components/shared/SafeErrorBoundary';
// ============================================================================// DEPRECATED
// // Structure follows Component_Path_Validation_Template: Purpose, Why changed, Replacement.
// ============================================================================
//
// 1. ErrorBoundary (was: components/shared/ErrorBoundary
//    Purpose: Caught React render errors and showed a fallback UI (with stack trace).
//    Why changed: Replaced by SafeErrorBoundary (cleaner UI, no stack in prod, retry/nav). Old ErrorBoundary also pulled in FailSafeAnalytics; we wanted a single error boundary and no deprecated deps.
//    Replacement: SafeErrorBoundary (imported above). Use it; do not re-add ErrorBoundary.
//
// 2. RoutingErrorHandler (was: components/shared/RoutingErrorHandler or components/deprecated/RoutingErrorHandler)
//    Purpose: Rendered on unknown routes and redirected the user (e.g. to map or home).
//    Why changed: Replaced by a single catch-all route and NotFoundRedirect component. Simpler; one place to change redirect behavior.
//    Replacement: NotFoundRedirect (defined below) and <Route path="*" element={<NotFoundRedirect />} />. Do not re-add RoutingErrorHandler.
//
// 3. components/deprecated/* (entire folder deleted 
//     Purpose: Old copies of error-handling and nav code: FailSafeAnalytics.js, FailSafeIntegration.js, globalErrorHandlers.js, routeValidation.js, NamingValidator.js, navigationUtils.js, ErrorBoundary_OLD.js, RoutingErrorHandler.js, ProtectedRoute.js, ChapterContainer.js, ShapePuzzle.js, TestClozeInput.js, VocabularyFeature.js, ErrorHandling_Deprecation_README.txt.
//    Why changed: Over-engineered; caused mix errors (conflicting boundaries, logging spirals). New system is SafeErrorBoundary + RawErrorService + validationSchemas. App never imported from deprecated/; folder was deleted so nobody uses it by mistake.
//    Replacement: For route protection use contexts/AuthContext (ProtectedRoute). For error UI use SafeErrorBoundary. For logging use RawErrorService. For chapter flow use the inline ChapterModuleRunner below. (ChapterContainer.js deleted).
// ============================================================================
// Upload
import UploadChapterLocations from './components/upload/UploadChapterLocations';
import { AuthProvider, ProtectedRoute } from './contexts/AuthContext';
import { UserKnowledgeProvider } from './contexts/UserKnowledgeContext';
import { getComponentFromRegistry } from './utils/componentRegistry';
import { getDefaultModuleMap } from './utils/componentScanner';
import './styles/App.css'; // Keep legacy for now during transition
// Error pipeline: global handlers use RawErrorService (writeRawErrorFromFailureContext). FailSafeAnalytics deprecated for error reporting.


const HangulPractice = React.lazy(() => import('./components/HangulPractice/HangulPractice'));
const ChapterPage = React.lazy(() => import('./components/navigation/ChapterPage'));
const UploadChapterAudio = React.lazy(() => import('./components/upload/UploadChapterAudio'));
const MarketingAnalytics = React.lazy(() => import('./components/Marketing/MarketingAnalytics'));
const MarketingPreviewPage = React.lazy(() => import('./components/Marketing/MarketingPreviewPage'));
const UnifiedFirestoreAdmin = React.lazy(() => import('./components/admin/UnifiedFirestoreAdmin'));
const UnifiedChapterAdmin = React.lazy(() => import('./components/admin/UnifiedChapterAdmin'));
const GrammarManagementAdmin = React.lazy(() => import('./components/admin/GrammarManagementAdmin'));
const MainAdmin = React.lazy(() => import('./components/admin/MainAdmin'));
// ChapterContainer DEPRECATED - replaced by inline ChapterModuleRunner below

// ============================================================================
// CHAPTER MODULE RUNNER (replaces deprecated ChapterContainer.js)
// ============================================================================
// moduleId -> componentName fallback for chapter configs whose moduleMap
// entries lack a component field (merged from the canonical default map)
const DEFAULT_COMPONENT_BY_MODULE = Object.fromEntries(
  Object.entries(getDefaultModuleMap())
    .map(([moduleId, config]) => [moduleId, config.component])
    .filter(([, component]) => Boolean(component))
);
// This inline component eliminates the ChapterContainer middleman that caused
// infinite re-render loops. It directly wires useChapterNavigation render.
const ChapterModuleRunner = () => {
  // Lazy-import hook at render time to avoid top-level circular deps
  const [hookModule, setHookModule] = React.useState(null);
  const [ModuleErrorFallbackComp, setModuleErrorFallback] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([
      import('./hooks/useChapterNavigation'),
      import('./components/learning/ModuleErrorFallback')
    ]).then(([hookMod, fallbackMod]) => {
      if (!cancelled) {
        setHookModule(() => hookMod.default);
        setModuleErrorFallback(() => fallbackMod.default);
      }
    });
    return () => { cancelled = true; };
  }, []);

  if (!hookModule) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#1a1a1a', color: '#fff' }}>
        <div className="loading-spinner" />
        <div style={{ opacity: 0.7 }}>Loading module...</div>
      </div>
    );
  }

  // Render the actual runner once hooks are loaded
  return <ChapterModuleRunnerInner useChapterNavigation={hookModule} ModuleErrorFallback={ModuleErrorFallbackComp} />;
};

const ChapterModuleRunnerInner = ({ useChapterNavigation, ModuleErrorFallback }) => {
  const {
    currentModuleKey,
    currentModuleConfig,
    currentIndex,
    linearSequence,
    loading,
    error,
    level,
    chapter,
    next,
    previous,
    menu
  } = useChapterNavigation();

  // Memoize navigation to prevent unnecessary re-renders in children
  const navigationValue = useMemo(() => ({
    next,
    previous,
    menu
  }), [next, previous, menu]);

  // 1. Loading State
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#1a1a1a', color: '#fff' }}>
        <div className="loading-spinner" />
        <div style={{ opacity: 0.7 }}>Loading module...</div>
      </div>
    );
  }

  // 2. Error State
  if (error) {
    return (
      <div style={{ padding: '2rem', color: 'white', background: '#1a1a1a', height: '100vh' }}>
        <h2>Configuration Error</h2>
        <p>{error.message || 'Failed to load chapter configuration'}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  // 2.5 Empty chapter: no module content configured in Firestore
  if (!linearSequence || linearSequence.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', height: '100vh', background: '#1a1a1a', color: '#fff', textAlign: 'center' }}>
        <h2>Chapter coming soon</h2>
        <p>This chapter doesn't have any learning content configured yet.</p>
        <button onClick={previous} style={{ padding: '0.6rem 1.4rem', cursor: 'pointer' }}>Back to Level</button>
      </div>
    );
  }

  // 3. Resolve Component from registry (with canonical fallback by moduleId)
  const componentName = currentModuleConfig?.component || DEFAULT_COMPONENT_BY_MODULE[currentModuleKey];
  const Component = componentName ? getComponentFromRegistry(componentName) : null;

  // 4. Fallback if Component or Module Key is missing
  if (!currentModuleKey || !Component) {
    return (
      <ModuleErrorFallback
        level={level}
        chapter={chapter}
        moduleKey={currentModuleKey || `Index ${currentIndex}`}
        error={{ message: `Component "${componentName}" not found in registry or module config missing` }}
        onSelectOverride={(path) => {
          console.log('Override intent logged:', path);
          alert('To initiate override, please use the UnifiedChapterFlowAdmin tool.');
        }}
      />
    );
  }

  // 6. Render Module same output as old ChapterContainer
  return (
    <div className="chapter-container-unified" style={{ width: '100%', height: '100vh', overflow: 'hidden' }}>
      <Suspense fallback={<div className="module-loading">Loading module...</div>}>
        <SafeErrorBoundary>
          <Component
            key={`${currentModuleKey}-${currentIndex}`}
            navigation={navigationValue}
            level={level}
            chapter={chapter}
            moduleKey={currentModuleKey}
            moduleConfig={currentModuleConfig}
            contentDocId={currentModuleConfig?.contentDocId || null}
            modulesOrder={[]}
            moduleMap={{}}
          />
        </SafeErrorBoundary>
      </Suspense>
    </div>
  );
};

// ============================================================================
// NOT FOUND REDIRECT COMPONENT (replaces removed RoutingErrorHandler)
// ============================================================================
// Suspense fallback for the chapter-page route: mirrors ChapterPage's cinematic
// layout so the screen keeps its shape while the lazy chunk downloads.
const ChapterRouteFallback = () => (
  <div className="chapter-page-container theme-cinematic theme-cinematic">
    <div className="chapter-background" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0a0a0c 100%)' }} />
    <div className="chapter-overlay" />
    <div className="chapter-main-content">
      <div className="chapter-brand">NUNCHI</div>
      <div className="module-list-container" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="module-card skeleton skeleton-card" />
        ))}
      </div>
      <div className="location-selector-container" aria-hidden="true">
        <div className="location-bar-inner">
          <div className="location-scroll-track">
            {[0, 1, 2].map((i) => (
              <div key={i} className="location-thumbnail skeleton skeleton-thumbnail" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const NotFoundRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    console.group('[App] NotFoundRedirect');
    console.log('[App] Unknown route detected, redirecting to map');
    console.log('[App] Replaces removed RoutingErrorHandler (deprecated).');
    console.groupEnd();
    navigate('/map', { replace: true });
  }, [navigate]);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: '16px'
    }}>
      <p>Redirecting to menu...</p>
    </div>
  );
};

console.log('Error handling: BootstrapErrorBoundary (index)')
// Backup static component registry for critical components
const _staticComponentRegistry = {
  'VocabularyFeature': VocabularyFeature,
  'GrammarFeature': GrammarFeature,
  'ClozeExercise': ClozeExercise,
  'TranslationMatching': TranslationMatching,
  'VocabularyLearn': VocabularyLearn,
  'GrammarLearn': GrammarLearn,
  'vocabularyLearn': VocabularyLearn,
  'grammarLearn': GrammarLearn,
  'ChapterReview': ChapterReview
};

//  USER KNOWLEDGE TRACKING SYSTEM
// Tracks user's grammar knowledge and prerequisites for adaptive learning

// User knowledge profile structure
export const createUserKnowledgeProfile = () => ({
  grammar_points: {
    basic_verb_conjugation: { mastered: false, attempts: 0, accuracy: 0 },
    polite_speech_endings: { mastered: false, attempts: 0, accuracy: 0 },
    informal_speech_endings: { mastered: false, attempts: 0, accuracy: 0 },
    honorific_forms: { mastered: false, attempts: 0, accuracy: 0 },
    past_tense_markers: { mastered: false, attempts: 0, accuracy: 0 }
  },
  vocabulary: {
    basic_verbs: { mastered: false, attempts: 0, accuracy: 0 },
    family_terms: { mastered: false, attempts: 0, accuracy: 0 },
    time_expressions: { mastered: false, attempts: 0, accuracy: 0 }
  },
  skills: {
    verb_stem_recognition: { mastered: false, attempts: 0, accuracy: 0 },
    speech_level_awareness: { mastered: false, attempts: 0, accuracy: 0 }
  },
  overall_level: 'beginner', // beginner, intermediate, advanced
  last_updated: new Date().toISOString()
});

// Check if user meets prerequisites for an exercise
export const checkPrerequisites = (exercise, userKnowledge) => {
  if (!exercise.prerequisites) return true; // No prerequisites = always available

  const { required_grammar_points = [], required_vocabulary = [], required_skills = [], minimum_level = 'beginner' } = exercise.prerequisites;

  // Check minimum level
  const levelHierarchy = { beginner: 0, intermediate: 1, advanced: 2 };
  if (levelHierarchy[userKnowledge.overall_level] < levelHierarchy[minimum_level]) {
    return false;
  }

  // Check grammar points
  for (const grammarPoint of required_grammar_points) {
    if (!userKnowledge.grammar_points[grammarPoint]?.mastered) {
      return false;
    }
  }

  // Check vocabulary
  for (const vocabCategory of required_vocabulary) {
    if (!userKnowledge.vocabulary[vocabCategory]?.mastered) {
      return false;
    }
  }

  // Check skills
  for (const skill of required_skills) {
    if (!userKnowledge.skills[skill]?.mastered) {
      return false;
    }
  }

  return true;
};

// Update user knowledge based on exercise performance
export const updateUserKnowledge = (userKnowledge, exercise, isCorrect, _responseTime) => {
  const updatedKnowledge = { ...userKnowledge };

  // Update grammar points
  if (exercise.prerequisites?.required_grammar_points) {
    exercise.prerequisites.required_grammar_points.forEach(grammarPoint => {
      if (!updatedKnowledge.grammar_points[grammarPoint]) {
        updatedKnowledge.grammar_points[grammarPoint] = { mastered: false, attempts: 0, accuracy: 0 };
      }

      const point = updatedKnowledge.grammar_points[grammarPoint];
      point.attempts++;
      point.accuracy = ((point.accuracy * (point.attempts - 1)) + (isCorrect ? 1 : 0)) / point.attempts;

      // Mark as mastered if accuracy > 80% and attempts >= 3
      if (point.accuracy > 0.8 && point.attempts >= 3) {
        point.mastered = true;
      }
    });
  }

  // Update vocabulary
  if (exercise.prerequisites?.required_vocabulary) {
    exercise.prerequisites.required_vocabulary.forEach(vocabCategory => {
      if (!updatedKnowledge.vocabulary[vocabCategory]) {
        updatedKnowledge.vocabulary[vocabCategory] = { mastered: false, attempts: 0, accuracy: 0 };
      }

      const vocab = updatedKnowledge.vocabulary[vocabCategory];
      vocab.attempts++;
      vocab.accuracy = ((vocab.accuracy * (vocab.attempts - 1)) + (isCorrect ? 1 : 0)) / vocab.attempts;

      if (vocab.accuracy > 0.8 && vocab.attempts >= 3) {
        vocab.mastered = true;
      }
    });
  }

  // Update skills
  if (exercise.prerequisites?.required_skills) {
    exercise.prerequisites.required_skills.forEach(skill => {
      if (!updatedKnowledge.skills[skill]) {
        updatedKnowledge.skills[skill] = { mastered: false, attempts: 0, accuracy: 0 };
      }

      const skillData = updatedKnowledge.skills[skill];
      skillData.attempts++;
      skillData.accuracy = ((skillData.accuracy * (skillData.attempts - 1)) + (isCorrect ? 1 : 0)) / skillData.attempts;

      if (skillData.accuracy > 0.8 && skillData.attempts >= 3) {
        skillData.mastered = true;
      }
    });
  }

  // Update overall level based on mastery
  const totalMastered = Object.values(updatedKnowledge.grammar_points).filter(p => p.mastered).length +
    Object.values(updatedKnowledge.vocabulary).filter(v => v.mastered).length +
    Object.values(updatedKnowledge.skills).filter(s => s.mastered).length;

  const totalItems = Object.keys(updatedKnowledge.grammar_points).length +
    Object.keys(updatedKnowledge.vocabulary).length +
    Object.keys(updatedKnowledge.skills).length;

  const masteryRatio = totalMastered / totalItems;

  if (masteryRatio > 0.8) {
    updatedKnowledge.overall_level = 'advanced';
  } else if (masteryRatio > 0.5) {
    updatedKnowledge.overall_level = 'intermediate';
  } else {
    updatedKnowledge.overall_level = 'beginner';
  }

  updatedKnowledge.last_updated = new Date().toISOString();

  return updatedKnowledge;
};

//  REUSABLE ERROR HANDLING UTILITIES
// These functions can be used by any component to handle errors gracefully

// Universal error logging with detailed context
export const logModuleError = (moduleName, errorType, error, additionalInfo = {}) => {
  console.group(`ERROR: Module ${moduleName} failed`);
  console.error('Error:', error);
  console.error('Error Type:', errorType);
  console.error('Module:', moduleName);
  console.error('Additional Info:', additionalInfo);
  console.error('Timestamp:', new Date().toISOString());
  console.error('User Agent:', navigator.userAgent);
  console.error('URL:', window.location.href);
  console.groupEnd();
};

// Universal navigation handler with error recovery (LEGACY VERSION - kept for backward compatibility)
export const createNavigationHandlerLegacy = (navigate, level, chapter, moduleName, modulesOrder = null) => {
  // Use provided modules order or fallback to default
  const moduleSequence = modulesOrder || ['dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle', 'complete'];

  return {
    // Navigate to next module in sequence
    next: () => {
      console.log(` ${moduleName}: Navigating to next module`);
      console.log(` Using modules order:`, moduleSequence);
      try {
        const currentIndex = moduleSequence.indexOf(moduleName);
        const nextModule = moduleSequence[currentIndex + 1];

        if (nextModule) {
          navigate(`/level/${level}/chapter/${chapter}/${nextModule}`);
        } else {
          // Last module, go to completion
          navigate(`/level/${level}/chapter/${chapter}/complete`);
        }
      } catch (error) {
        logModuleError(moduleName, 'Navigation Error', error, { action: 'next' });
        navigate(`/level/${level}`);
      }
    },

    // Navigate to previous module
    previous: () => {
      console.log(` ${moduleName}: Navigating to previous module`);
      console.log(` Using modules order:`, moduleSequence);
      try {
        const currentIndex = moduleSequence.indexOf(moduleName);
        const prevModule = moduleSequence[currentIndex - 1];

        if (prevModule) {
          navigate(`/level/${level}/chapter/${chapter}/${prevModule}`);
        } else {
          // First module, go to level page
          navigate(`/level/${level}`);
        }
      } catch (error) {
        logModuleError(moduleName, 'Navigation Error', error, { action: 'previous' });
        navigate(`/level/${level}`);
      }
    },

    // Navigate to menu
    menu: () => {
      console.log(` ${moduleName}: Navigating to menu`);
      try {
        navigate('/app-homepage');
      } catch (error) {
        logModuleError(moduleName, 'Navigation Error', error, { action: 'menu' });
        window.location.href = '/app-homepage';
      }
    }
  };
};

// Universal error state component - ALWAYS preserves navigation
export const ErrorState = ({
  moduleName,
  errorType,
  error,
  level,
  chapter,
  navigation,
  additionalInfo = {}
}) => {
  logModuleError(moduleName, errorType, error, additionalInfo);

  return (
    <div className="universal-module-container">
      {/* Universal Module Header - ALWAYS show navigation */}
      <div className="exercise-top-navigation">
        <div className="module-info">
          <span>Level {level}</span>
          <span className="separator"></span>
          <span>Chapter {chapter}</span>
          <span className="separator"></span>
          <span>{moduleName.charAt(0).toUpperCase() + moduleName.slice(1)} Module</span>
        </div>

        <div className="module-nav-buttons">
          <button className="module-nav-btn secondary" onClick={navigation.previous}>
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
          <span className="separator">|</span>
          <button className="module-nav-btn secondary" onClick={navigation.menu}>
            <i className="fas fa-home"></i>
            Menu
          </button>
          <span className="separator">|</span>
          <button className="module-nav-btn" onClick={navigation.next}>
            <i className="fas fa-arrow-right"></i>
            Next
          </button>
        </div>
      </div>

      <div className="module-content">
        <div className="error-container">
          <h3> {moduleName.charAt(0).toUpperCase() + moduleName.slice(1)} Module Error</h3>
          <p>The {moduleName} module encountered an unexpected error.</p>

          {error && (
            <div className="error-details" style={{
              background: '#f8f9fa',
              padding: '1rem',
              borderRadius: '4px',
              margin: '1rem 0',
              fontSize: '0.9rem',
              color: '#666'
            }}>
              <strong>Error Details:</strong>
              <pre style={{
                margin: '0.5rem 0',
                whiteSpace: 'pre-wrap',
                fontSize: '0.8rem'
              }}>
                {error.message || error.toString()}
              </pre>
            </div>
          )}

          <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem' }}>
            This might be due to data loading issues, network problems, or Firebase connectivity issues.
            You can retry the module or continue to the next one.
          </p>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button
              className="start-button"
              onClick={() => window.location.reload()}
            >
              Retry Module
            </button>

            <button
              className="start-button secondary"
              onClick={navigation.next}
            >
              Skip to Next Module
            </button>

            <button
              className="start-button secondary"
              onClick={navigation.menu}
            >
              Return to Menu
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <details className="error-details" style={{ marginTop: '1rem' }}>
              <summary>Development Debug Info</summary>
              <pre style={{
                fontSize: '0.8rem',
                background: '#f5f5f5',
                padding: '1rem',
                borderRadius: '4px',
                whiteSpace: 'pre-wrap'
              }}>
                {JSON.stringify({
                  module: moduleName,
                  level,
                  chapter,
                  errorType,
                  error: error?.message || error?.toString(),
                  additionalInfo
                }, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

// Universal no data state component - ALWAYS preserves navigation
export const NoDataState = ({
  moduleName,
  level,
  chapter,
  navigation,
  message = 'Content is not available yet'
}) => {
  console.log(` ${moduleName.toUpperCase()}: No data available for Level ${level} Chapter ${chapter}`);

  return (
    <div className="universal-module-container">
      {/* Universal Module Header - ALWAYS show navigation */}
      <div className="exercise-top-navigation">
        <div className="module-info">
          <span>Level {level}</span>
          <span className="separator"></span>
          <span>Chapter {chapter}</span>
          <span className="separator"></span>
          <span>{moduleName.charAt(0).toUpperCase() + moduleName.slice(1)} Module</span>
        </div>

        <div className="module-nav-buttons">
          <button className="module-nav-btn secondary" onClick={navigation.previous}>
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
          <span className="separator">|</span>
          <button className="module-nav-btn secondary" onClick={navigation.menu}>
            <i className="fas fa-home"></i>
            Menu
          </button>
          <span className="separator">|</span>
          <button className="module-nav-btn" onClick={navigation.next}>
            <i className="fas fa-arrow-right"></i>
            Next
          </button>
        </div>
      </div>

      <div className="module-content">
        <div className="no-data-container">
          <h3> No {moduleName.charAt(0).toUpperCase() + moduleName.slice(1)} Content Available</h3>
          <p>{message}</p>
          <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem' }}>
            You can still continue to the next module.
          </p>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button
              className="start-button"
              onClick={() => window.location.reload()}
            >
              Retry Module
            </button>

            <button
              className="start-button secondary"
              onClick={navigation.next}
            >
              Skip to Next Module
            </button>

            <button
              className="start-button secondary"
              onClick={navigation.menu}
            >
              Return to Menu
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

//  CHAPTER COMPLETION WRAPPER
const ChapterCompletionWrapper = () => {
  const { level, chapter } = useParams();
  const navigate = useNavigate();

  // Create navigation handler for ChapterCompletion
  const navigation = {
    next: () => {
      const currentChapter = parseInt(chapter);
      const currentLevel = parseInt(level);
      if (currentChapter < 10) {
        navigate(`/level/${currentLevel}/chapter/${currentChapter + 1}`);
      } else {
        navigate(`/level/${currentLevel + 1}/chapter/1`);
      }
    },
    previous: () => navigate(`/level/${level}/chapter/${chapter}`),
    menu: () => navigate('/app-homepage')
  };

  return (
    <ChapterCompletion
      level={level}
      chapter={chapter}
      navigation={navigation}
      modulesOrder={null}
      moduleMap={null}
      moduleKey="complete"
      moduleConfig={{}}
    />
  );
};

//  NAVIGATION HANDLER (updated for new structure with correct field naming)
export const createNavigationHandler = (navigate, level, chapter, moduleName, modulesOrder = null, moduleMap = null) => {
  const moduleSequence = modulesOrder || ['chapterTitle', 'dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle', 'complete'];

  return {
    next: () => {
      try {
        // NEW SYSTEM: Return to ChapterPage (the map hub) instead of skipping to next module
        // This allows users to see their progress on the map and unlocks in real-time
        console.log('Hub Navigation: Returning to ChapterPage');
        navigate(`/chapter-page/${level}/${chapter}`);
      } catch (error) {
        logModuleError(moduleName, 'Navigation Error', error, { action: 'next' });
        navigate(`/level/${level}`);
      }
    },
    previous: () => {
      try {
        const currentIndex = moduleSequence.indexOf(moduleName);
        const prevModule = moduleSequence[currentIndex - 1];
        if (prevModule) {
          const routePath = moduleMap?.[prevModule]?.routePath || prevModule;
          navigate(`/level/${level}/chapter/${chapter}/${routePath}`);
        } else {
          navigate(`/level/${level}`);
        }
      } catch (error) {
        logModuleError(moduleName, 'Navigation Error', error, { action: 'previous' });
        navigate(`/level/${level}`);
      }
    },
    menu: () => {
      try {
        navigate('/app-homepage');
      } catch (error) {
        logModuleError(moduleName, 'Navigation Error', error, { action: 'menu' });
        window.location.href = '/app-homepage';
      }
    }
  };
};

// FIXED: Better component registry with dynamic imports
const componentRegistry = {
  'ChapterTitleScreen': React.lazy(() =>
    import('./components/navigation/ChapterTitleScreen').catch(err => {
      console.error('Failed to load ChapterTitleScreen:', err);
      return { default: () => <div>ChapterTitleScreen failed to load</div> };
    })
  ),
  'DialogueFeature': React.lazy(() =>
    import('./components/learning/DialogueFeature').catch(err => {
      console.error('Failed to load DialogueFeature:', err);
      return { default: () => <div>DialogueFeature failed to load</div> };
    })
  ),
  'DialogueVocabV2': React.lazy(() =>
    import('./components/learning/DialogueVocabV2').catch(err => {
      console.error('Failed to load DialogueVocabV2:', err);
      return { default: () => <div>DialogueVocabV2 failed to load</div> };
    })
  ),
  'DialogueUniversal': React.lazy(() =>
    import('./components/learning/DialogueUniversal').catch(err => {
      console.error('Failed to load DialogueUniversal:', err);
      return { default: () => <div>DialogueUniversal failed to load</div> };
    })
  ),
  'VocabularyFeature': React.lazy(() =>
    import('./components/learning/VocabularyFeature').catch(err => {
      console.error('Failed to load VocabularyFeature:', err);
      return { default: () => <div>VocabularyFeature failed to load</div> };
    })
  ),
  'TranslationMatching': React.lazy(() =>
    import('./components/exercises/TranslationMatching').catch(err => {
      console.error('Failed to load TranslationMatching:', err);
      return { default: () => <div>TranslationMatching failed to load</div> };
    })
  ),
  'GrammarFeature': React.lazy(() =>
    import('./components/exercises/GrammarFeature').catch(err => {
      console.error('Failed to load GrammarFeature:', err);
      return { default: () => <div>GrammarFeature failed to load</div> };
    })
  ),
  'ClozeExercise': React.lazy(() =>
    import('./components/exercises/ClozeExercise').catch(err => {
      console.error('Failed to load ClozeExercise:', err);
      return { default: () => <div>ClozeExercise failed to load</div> };
    })
  ),

  'VocabularyLearn': React.lazy(() =>
    import('./components/learning/VocabularyLearn').catch(err => {
      console.error('Failed to load VocabularyLearn:', err);
      return { default: () => <div>VocabularyLearn failed to load</div> };
    })
  ),
  'vocabularyLearn': React.lazy(() =>
    import('./components/learning/VocabularyLearn').catch(err => {
      console.error('Failed to load vocabularyLearn:', err);
      return { default: () => <div>vocabularyLearn failed to load</div> };
    })
  ),
  'GrammarLearn': React.lazy(() =>
    import('./components/learning/GrammarLearn').catch(err => {
      console.error('Failed to load GrammarLearn:', err);
      return { default: () => <div>GrammarLearn failed to load</div> };
    })
  ),
  'grammarLearn': React.lazy(() =>
    import('./components/learning/GrammarLearn').catch(err => {
      console.error('Failed to load grammarLearn:', err);
      return { default: () => <div>grammarLearn failed to load</div> };
    })
  ),
  'ChapterCompletion': React.lazy(() =>
    import('./components/navigation/ChapterCompletion').catch(err => {
      console.error('Failed to load ChapterCompletion:', err);
      return { default: () => <div>ChapterCompletion failed to load</div> };
    })
  ),
  'HangulPractice': React.lazy(() =>
    import('./components/HangulPractice/HangulPractice').catch(err => {
      console.error('Failed to load HangulPractice:', err);
      return { default: () => <div>HangulPractice failed to load</div> };
    })
  ),
  'ReviewGenerator': React.lazy(() => import('./components/learning/ReviewGenerator')),
  'TeachModule': React.lazy(() => import('./components/TeachModule/TeachModule')),
  'VocabTeachModule': React.lazy(() => import('./components/TeachModule/VocabTeachModule')),
  'AdvertTeachModule': React.lazy(() => import('./components/TeachModule/AdvertTeachModule')),
  'ComicReader': React.lazy(() => import('./components/ComicReader/ComicReader')),
  'ShapePuzzle': React.lazy(() =>
    import('./components/deprecated/ShapePuzzleNew').catch(err => {
      console.error('Failed to load ShapePuzzle:', err);
      return { default: () => <div>ShapePuzzle failed to load</div> };
    })
  ),
  'ShapePuzzleNew': React.lazy(() =>
    import('./components/deprecated/ShapePuzzleNew').catch(err => {
      console.error('Failed to load ShapePuzzleNew:', err);
      return { default: () => <div>ShapePuzzleNew failed to load</div> };
    })
  )
};

// Review Player Wrapper
const ReviewPlayer = () => {
  const { level, chapter, scenarioId } = useParams();
  const navigate = useNavigate();
  const [moduleConfig, setModuleConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [completionData, setCompletionData] = useState(null);

  console.group(' ReviewPlayer: Component loaded');
  console.log('Level:', level, 'Chapter:', chapter, 'ScenarioId:', scenarioId);

  // Load scenario from Firestore using scenarioId from URL params
  useEffect(() => {
    const loadScenario = async () => {
      if (!scenarioId || !level || !chapter) {
        setError('Missing scenario ID, level, or chapter');
        setLoading(false);
        return;
      }

      try {
        console.log(' Loading scenario from Firestore:', scenarioId);
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./services/firebase');

        // Construct Firestore path for DOCUMENT:
        // feature_testing/Korea_University/Level{level}/Dialogue/Chapter{chapter}/{scenarioId}
        const firestorePath = `feature_testing/Korea_University/Level${level}/Dialogue/Chapter${chapter}/${scenarioId}`;
        const pathSegments = firestorePath.split('/').filter(Boolean);

        // Firestore document paths must have an EVEN number of segments:
        // collection/doc, collection/doc/collection/doc, etc.
        if (pathSegments.length % 2 !== 0) {
          throw new Error(`Invalid Firestore path: document paths must have EVEN segments, got ${pathSegments.length}`);
        }

        const scenarioRef = doc(db, ...pathSegments);
        const scenarioSnap = await getDoc(scenarioRef);

        if (!scenarioSnap.exists()) {
          throw new Error(`Scenario not found: ${scenarioId}`);
        }

        const scenarioData = scenarioSnap.data();
        console.log(' Scenario loaded successfully:', scenarioData);

        // Construct moduleConfig from loaded data
        const config = {
          displayName: scenarioData.title || scenarioData.metadata?.title || 'Review Session',
          type: scenarioData.metadata?.type || 'review',
          contentDocPath: firestorePath,
          reviewMode: scenarioData.metadata?.mode || 'GeneralReview',
          scenarioData: scenarioData // Store full data for reference
        };

        setModuleConfig(config);
        setLoading(false);

        // Add to browser history for back button support
        window.history.replaceState(
          { scenarioId, level, chapter, loaded: true },
          '',
          window.location.href
        );

      } catch (err) {
        console.error(' Error loading scenario:', err);
        setError(err.message || 'Failed to load scenario');
        setLoading(false);
      }
    };

    loadScenario();
  }, [scenarioId, level, chapter]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      console.log(' Browser back button pressed');
      if (event.state && event.state.scenarioId) {
        // State preserved, allow normal back navigation
        console.log(' History state preserved, allowing back navigation');
      } else {
        // No state, navigate to review generator
        console.log(' No history state, navigating to review generator');
        navigate(`/level/${level}/chapter/${chapter}/review`, { replace: true });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [level, chapter, navigate]);

  // Create navigation handlers with completion screen support
  const navigation = useMemo(() => ({
    next: (completionData = null) => {
      if (completionData) {
        // Show completion screen before returning
        setCompletionData(completionData);
        setShowCompletion(true);
      } else {
        // Direct navigation (for previous button)
        navigate(`/level/${level}/chapter/${chapter}/review`, { replace: false });
      }
    },
    previous: () => {
      // Go back in browser history if available, otherwise go to review generator
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(`/level/${level}/chapter/${chapter}/review`, { replace: false });
      }
    },
    menu: () => navigate('/app-homepage', { replace: false }),
    showCompletion: (data) => {
      setCompletionData(data);
      setShowCompletion(true);
    }
  }), [level, chapter, navigate]);

  // Completion screen component
  const CompletionScreen = () => {
    if (!showCompletion) return null;

    const handleReturnToGenerator = () => {
      setShowCompletion(false);
      navigate(`/level/${level}/chapter/${chapter}/review`, { replace: false });
    };

    const handlePlayAgain = () => {
      setShowCompletion(false);
      // Reload the same scenario
      window.location.reload();
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: '2rem',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{ marginTop: 0, color: '#4CAF50' }}>
            <i className="fas fa-check-circle"></i> Review Complete!
          </h2>

          {completionData && (
            <div style={{ margin: '1.5rem 0' }}>
              {completionData.finalScore !== undefined && (
                <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                  <strong>Final Score:</strong> {completionData.finalScore.correct || 0} / {completionData.finalScore.total || 0}
                </div>
              )}
              {completionData.accuracy !== undefined && (
                <div style={{ fontSize: '1.2rem', color: '#666', marginBottom: '1rem' }}>
                  Accuracy: {completionData.accuracy}%
                </div>
              )}
              {completionData.totalSteps && (
                <div style={{ fontSize: '1rem', color: '#666' }}>
                  Completed {completionData.completedSteps || 0} of {completionData.totalSteps} steps
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
            <button
              className="start-button"
              onClick={handleReturnToGenerator}
              style={{ padding: '0.75rem 1.5rem' }}
            >
              <i className="fas fa-arrow-left"></i> Back to Generator
            </button>
            <button
              className="start-button secondary"
              onClick={handlePlayAgain}
              style={{ padding: '0.75rem 1.5rem' }}
            >
              <i className="fas fa-redo"></i> Play Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="error-container" style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="loading-screen">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
          <p>Loading review scenario...</p>
        </div>
      </div>
    );
  }

  if (error || !moduleConfig) {
    console.error(' ReviewPlayer: Error loading scenario', error);
    console.groupEnd();
    return (
      <div className="error-container" style={{ padding: '2rem', textAlign: 'center' }}>
        <h3> Error: {error || 'Failed to load scenario'}</h3>
        <p>Scenario ID: {scenarioId}</p>
        <p>Please start from the Review Generator menu.</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button
            className="start-button"
            onClick={() => navigate(`/level/${level}/chapter/${chapter}/review`)}
          >
            Go to Review Generator
          </button>
          <button
            className="start-button secondary"
            onClick={() => navigate('/app-homepage')}
          >
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  console.log(' ReviewPlayer: Module config loaded:', moduleConfig);
  console.groupEnd();

  const DialogueUniversalComponent = componentRegistry['DialogueUniversal'];
  return (
    <>
      <CompletionScreen />
      <Suspense fallback={<div className="loading-screen">Loading Dialogue...</div>}>
        <DialogueUniversalComponent
          level={level}
          chapter={chapter}
          moduleKey="review_session"
          moduleConfig={moduleConfig}
          navigation={navigation}
          modulesOrder={['review_session']}
          moduleMap={{ review_session: moduleConfig }}
        />
      </Suspense>
    </>
  );
};

// ----------------------------------------------------------------------------
// End of file helpers
// ----------------------------------------------------------------------------


//  FAIL-SAFE 2: Dynamic Promise Rejection Handler
window.addEventListener('unhandledrejection', async (event) => {
  console.group(' DYNAMIC ERROR HANDLER: Unhandled Promise Rejection');
  console.error('Promise Rejection:', event.reason);
  console.error('URL:', window.location.href);
  console.groupEnd();

  // Prevent the default browser behavior
  event.preventDefault();

  // Use dynamic fail-safe analytics to handle the rejection
  const failureContext = {
    type: 'promise_rejection',
    errorType: event.reason?.message?.includes('Failed to fetch') ? 'network_error' : 'promise_error',
    component: 'system',
    error: event.reason,
    message: event.reason?.message || 'Unknown promise rejection',
    url: window.location.href,
    userAgent: navigator.userAgent,
    networkStatus: navigator.onLine ? 'online' : 'offline'
  };

  await writeRawErrorFromFailureContext(failureContext);
});

//  FAIL-SAFE 3: Dynamic Network Error Detection and Recovery
const networkErrorHandler = async (event) => {
  const isOnline = event.type === 'online';
  console.log(` Network ${isOnline ? 'online' : 'offline'} detected`);

  const failureContext = {
    type: 'network_status_change',
    errorType: isOnline ? 'network_restored' : 'network_lost',
    component: 'system',
    networkStatus: isOnline ? 'online' : 'offline',
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  };

  if (isOnline) {
    // Network restored - check if recovery is needed
    const offlineLocation = localStorage.getItem('offline_location');
    if (offlineLocation && offlineLocation !== window.location.href) {
      failureContext.offlineLocation = offlineLocation;
      localStorage.removeItem('offline_location');
    }
  } else {
    // Network lost - store current location
    localStorage.setItem('offline_location', window.location.href);
  }

  await writeRawErrorFromFailureContext(failureContext);
};

window.addEventListener('online', networkErrorHandler);
window.addEventListener('offline', networkErrorHandler);

//  FAIL-SAFE 4: Dynamic Route Validation and Auto-Recovery (errors go to RawErrorService)
const validateRoute = async () => {
  const currentPath = window.location.pathname;

  const baseRoutes = ['/', '/setup-password', '/assessment', '/complete', '/dashboard', '/app-homepage', '/about', '/map', '/contact', '/contact-card'];
  const dynamicRoutes = ['/level/', '/chapter/'];
  const staticRoutes = ['/favicon.ico', '/static/'];

  let isValidRoute = baseRoutes.includes(currentPath) ||
    staticRoutes.some(route => currentPath.startsWith(route)) ||
    dynamicRoutes.some(route => currentPath.startsWith(route)) ||
    currentPath.match(/^\/level\/\d+\/chapter\/\d+/);

  if (!isValidRoute) {
    const failureContext = {
      type: 'invalid_route',
      errorType: 'route_validation_failed',
      component: 'routing',
      message: `Invalid route: ${currentPath}`,
      url: typeof window !== 'undefined' ? window.location.href : 'N/A',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      networkStatus: typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline'
    };

    console.log(' Invalid route detected:', currentPath);
    await writeRawErrorFromFailureContext(failureContext);
    return false;
  }

  return true;
};

// PRE-DEPLOYMENT: App Component with Enhanced Fail-Safes
function App() {
  console.group('PRE-DEPLOYMENT: App component loaded v1.9.11');
  console.log('Pre-deployment environment initialized (v1.9.11)');
  const buildTimestamp = new Date().toISOString();
  console.log('Build Freshness Timestamp:', buildTimestamp);
  console.log('Environment:', process.env.NODE_ENV);
  console.groupEnd();

  // Validate route on app load
  React.useEffect(() => {
    validateRoute();
  }, []);
  return (
    <Router>
      <AuthProvider>
        <UserKnowledgeProvider>
          <SafeErrorBoundary>
            <div className="pre-deployment-app">
              <Routes>
                <Route path="/" element={<EmailEntry />} />
                <Route path="/setup-password" element={<PasswordSetup />} />
                <Route path="/assessment" element={<KoreanAssessment />} />
                <Route path="/complete" element={<ProfileComplete />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/app-homepage" element={<ProtectedRoute><AppHomepage /></ProtectedRoute>} />
                <Route path="/modern-level-select" element={<ProtectedRoute><ModernLevelSelect /></ProtectedRoute>} />
                <Route path="/map" element={<ProtectedRoute><MapMenu /></ProtectedRoute>} />
                <Route path="/about" element={<ProtectedRoute><About /></ProtectedRoute>} />
                <Route path="/contact" element={<ContactLanding />} />
                <Route path="/contact-card" element={<ContactCard />} />
                <Route path="/admin/main" element={
                  <ProtectedRoute admin={true}>
                    <Suspense fallback={<div className="loading-screen">Loading Admin Portal...</div>}>
                      <MainAdmin />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/admin/weekly-tasks" element={
                  <ProtectedRoute admin={true}>
                    <Suspense fallback={<div className="loading-screen">Loading Weekly Tasks...</div>}>
                      <MainAdmin />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/admin/chapter-locations" element={<Suspense fallback={<div>Loading...</div>}><UploadChapterLocations /></Suspense>} />
                <Route path="/admin/upload-audio" element={<Suspense fallback={<div>Loading...</div>}><UploadChapterAudio /></Suspense>} />
                <Route path="/admin/marketing-analytics" element={<Suspense fallback={<div>Loading...</div>}><MarketingAnalytics /></Suspense>} />
                <Route path="/admin/marketing/preview/:adId" element={<Suspense fallback={<div>Loading Preview...</div>}><MarketingPreviewPage /></Suspense>} />
                <Route path="/admin/firestore" element={<Suspense fallback={<div>Loading Firestore Admin...</div>}><UnifiedFirestoreAdmin /></Suspense>} />
                <Route path="/admin/chapter-flow" element={<Suspense fallback={<div>Loading Chapter Flow...</div>}><UnifiedChapterAdmin /></Suspense>} />
                <Route path="/admin/grammar-management" element={<Suspense fallback={<div>Loading Grammar Management...</div>}><GrammarManagementAdmin /></Suspense>} />
                <Route path="/test-bench" element={<ComponentTestBench />} />
                <Route path="/test-module/:name/:level/:chapter" element={<ComponentTestView />} />
                <Route path="/hangul-practice/*" element={
                  <ProtectedRoute>
                    <Suspense fallback={<div className="loading-screen">Loading Hangul Practice...</div>}>
                      <HangulPractice />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/level/:level" element={<ProtectedRoute><LevelPage /></ProtectedRoute>} />
                <Route path="/chapter-page/:level/:chapter" element={
                  <ProtectedRoute>
                    <Suspense fallback={<ChapterRouteFallback />}>
                      <ChapterPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/level/:level/chapter/:chapter" element={<ProtectedRoute><ChapterModuleRunner /></ProtectedRoute>} />
                <Route path="/level/:level/chapter/:chapter/complete" element={<ProtectedRoute><ChapterCompletionWrapper /></ProtectedRoute>} />
                <Route
                  path="/level/:level/chapter/:chapter/review"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<div>Loading Review Generator...</div>}>
                        <componentRegistry.ReviewGenerator />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route path="/level/:level/chapter/:chapter/play-review/:scenarioId" element={<ProtectedRoute><ReviewPlayer /></ProtectedRoute>} />
                <Route path="/teach/:mode/:level/:chapter/*" element={
                  <ProtectedRoute>
                    <Suspense fallback={<div>Loading Teach Module...</div>}>
                      <componentRegistry.TeachModule />
                    </Suspense>
                  </ProtectedRoute>
                } />
                {/* Catch-all: NotFoundRedirect (replaces removed RoutingErrorHandler) */}
                <Route path="*" element={<NotFoundRedirect />} />
              </Routes>
            </div>
          </SafeErrorBoundary>
        </UserKnowledgeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;




































































